import Tooltip, { tooltipClasses } from '@mui/material/Tooltip'
import { styled } from '@mui/material/styles'
import { GridEditInputCell, GridEditSingleSelectCell } from '@mui/x-data-grid'

export const ERROR_MESSAGES = {
  REQUIRED: 'Requerido',
  INVALID_NUMBER: 'Debe ser un número válido',
  POSITIVE_NUMBER: 'Valor debe ser mayor a 0',
  PERCENTAGE_RANGE: 'Valor debe estar entre 1 y 99',
  REQUIRED_CARBON_REPLICA: 'Se requiere un valor replica de carbono',
}

export const validateSingleSelectRequired = (value, error) => (value === undefined ? error : null)

export const validateInputStringRequired = (value, error) =>
  !value || value.trim() === '' ? error : null

export const validatePercentageNumber = ({ value, column = null }) => {
  const isRequired = column?.required !== undefined ? column.required : true

  if (value === null || value === undefined) {
    return isRequired ? ERROR_MESSAGES.REQUIRED : null
  }
  const num = Number(value)
  if (Number.isNaN(num)) {
    return ERROR_MESSAGES.INVALID_NUMBER
  }
  if (num < 0 || num > 100) {
    return ERROR_MESSAGES.PERCENTAGE_RANGE
  }
  return null
}

export const validatePositiveNumberRequired = ({
  value,
  allowZero = false,
  range = null,
  column = null,
}) => {
  const isRequired = column?.required !== undefined ? column.required : true

  if (value === null || value === undefined) {
    return isRequired ? ERROR_MESSAGES.REQUIRED : null
  }
  const num = Number(value)
  if (Number.isNaN(num)) {
    return ERROR_MESSAGES.INVALID_NUMBER
  }
  if (num === 0 && allowZero) {
    return null
  }
  if (num < 0) {
    return ERROR_MESSAGES.POSITIVE_NUMBER
  }

  if (range && (num < range.min || num > range.max)) {
    return `Valor debe estar entre ${range.min} y ${range.max}`
  }

  return null
}

export const validateCarbonReplicaRequired = ({ value, row, column }) => {
  const hasError = validatePercentageNumber({ value, column })
  if (hasError) return hasError

  const requiredCarbonoReplicaIsNOTLoaded = [
    row.wbFirstReplicaPercentage,
    row.wbSecondReplicaPercentage,
    row.caFirstReplicaPercentage,
    row.caSecondReplicaPercentage,
    row.lecoFirstReplicaPercentage,
    row.lecoSecondReplicaPercentage,
  ].every(replicaValue => [null, undefined].includes(replicaValue))

  return requiredCarbonoReplicaIsNOTLoaded ? ERROR_MESSAGES.REQUIRED_CARBON_REPLICA : null
}

export const validateSoilTexturePercentages = ({ value, row, column }) => {
  const percentageError = validatePercentageNumber({ value, column })
  if (percentageError) return percentageError

  const siltValue = parseFloat(row.siltPercentage) || 0
  const clayValue = parseFloat(row.clayPercentage) || 0
  const sandValue = parseFloat(row.sandPercentage) || 0

  const currentField = column?.field

  let sum
  switch (currentField) {
    case 'siltPercentage':
      sum = parseFloat(value) + clayValue + sandValue
      break
    case 'clayPercentage':
      sum = siltValue + parseFloat(value) + sandValue
      break
    case 'sandPercentage':
      sum = siltValue + clayValue + parseFloat(value)
      break
    default:
      sum = siltValue + clayValue + sandValue
  }

  if (sum > 100) {
    return 'La suma de Arcilla, Limo y Arena debe ser 100%'
  }

  return null
}

export const validateSandPercentage = ({ value, row, column }) => {
  const percentageError = validatePercentageNumber({ value, column })
  if (percentageError) return percentageError

  const fineSandValue = parseFloat(row.fineSandPercentage) || 0
  const coarseSandValue = parseFloat(row.coarseSandPercentage) || 0

  if (fineSandValue > 0 && coarseSandValue > 0) {
    const expectedSandValue = fineSandValue + coarseSandValue
    const actualSandValue = parseFloat(value) || 0

    if (Math.abs(actualSandValue - expectedSandValue) > 0.01) {
      return `El valor debe ser igual a la suma de Arena fina y Arena gruesa (${expectedSandValue.toFixed(2)})`
    }
  }

  return null
}

export const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    classes={{ popper: className }}
    slotProps={{ popper: { modifiers: [{ name: 'offset', options: { offset: [0, -12] } }] } }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
}))

const CellWrapper = styled('span')`
  display: flex;
  width: 100%;
  height: 100%;
`

const EditInputCell = ({ params, props }) => {
  const errors = params?.row?.errors?.[params?.field] ?? null

  const paramsInputProps = params.inputProps ?? {}

  const inputProps =
    params.colDef?.type === 'number'
      ? {
          onWheel: event => {
            event.currentTarget.blur()
          },
        }
      : {}

  return (
    <StyledTooltip open={!!errors} title={errors} {...props?.tooltip}>
      <CellWrapper>
        <GridEditInputCell {...params} inputProps={{ ...paramsInputProps, ...inputProps }} />
      </CellWrapper>
    </StyledTooltip>
  )
}

const EditSingleSelectCell = ({ params, props }) => {
  const errors = params?.row?.errors?.[params?.field] ?? null
  const value = params.value ?? ''

  const selectOption = { value: '', label: 'Seleccionar...' }
  const currentValueOptions = params.colDef?.valueOptions || []

  const valueOptionsWithDefault =
    typeof currentValueOptions === 'function'
      ? currentParams => {
          const options = currentValueOptions(currentParams)
          return [selectOption, ...options]
        }
      : [selectOption, ...currentValueOptions]

  const modifiedParams = {
    ...params,
    colDef: {
      ...params.colDef,
      valueOptions: valueOptionsWithDefault,
    },
  }

  return (
    <StyledTooltip open={!!errors} title={errors} {...props?.tooltip}>
      <CellWrapper>
        <GridEditSingleSelectCell defaultValue="" {...modifiedParams} value={value} />
      </CellWrapper>
    </StyledTooltip>
  )
}

const TextCell = ({ params, props }) => {
  const errors = params?.row?.errors?.[params?.field] ?? null

  return (
    <StyledTooltip open={!!errors} title={errors} {...props?.tooltip}>
      <CellWrapper>{params.value ?? ''}</CellWrapper>
    </StyledTooltip>
  )
}

const SelectCell = ({ params, props }) => {
  const errors = params?.row?.errors?.[params?.field] ?? null

  return (
    <StyledTooltip open={!!errors} title={errors} {...props?.tooltip}>
      <CellWrapper>
        <div>{params.formattedValue ?? ''}</div>
      </CellWrapper>
    </StyledTooltip>
  )
}
export function renderInput({ params, props }) {
  return <EditInputCell params={params} props={props} />
}

export function renderEditSingleSelect({ params, props }) {
  return <EditSingleSelectCell params={params} props={props} />
}

export function renderText({ params, props }) {
  return <TextCell params={params} props={props} />
}

export function renderSelect({ params, props }) {
  return <SelectCell params={params} props={props} />
}
